import { Form, Formik, useField } from "formik";
import * as Yup from "yup";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { Client } from "@/features/clients/types";
import {
  FILE_AS_OPTIONS,
  type FileAs,
  type Form433HData,
  type FormSubmission,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox, FormikSelect } from "./formik-controls";

const PAYMENT_DAYS = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const money = Yup.string().test(
  "non-negative",
  "Must be zero or greater",
  (v) => v == null || v === "" || (!Number.isNaN(Number(v)) && Number(v) >= 0),
);

const schema = Yup.object({
  for_who: Yup.string().oneOf(["T", "S", "J"]).required(),
  total_amount: money,
  add_total_amount: money,
  tax_amt_pay: money,
  tax_pay_per_month: money,
  routing_number: Yup.string()
    .matches(/^\d*$/, "Digits only")
    .test("len", "Must be 9 digits", (v) => !v || v.replace(/\D/g, "").length === 9),
  account_number: Yup.string().matches(/^\d*$/, "Digits only").max(17, "At most 17 digits"),
});

function toInitialValues(data: Record<string, unknown>): Form433HData {
  const d = data as Partial<Form433HData>;
  return {
    for_who: d.for_who ?? "T",
    tax_form_no: d.tax_form_no ?? "",
    tax_periods: d.tax_periods ?? "",
    total_amount: d.total_amount ?? "0",
    add_tax_form_no: d.add_tax_form_no ?? "",
    add_tax_periods: d.add_tax_periods ?? "",
    add_total_amount: d.add_total_amount ?? "0",
    tax_amt_pay: d.tax_amt_pay ?? "0",
    tax_pay_per_month: d.tax_pay_per_month ?? "0",
    tax_pay_date: d.tax_pay_date ?? "",
    no_electronic: d.no_electronic ?? false,
    payroll_deduction: d.payroll_deduction ?? false,
    routing_number: d.routing_number ?? "",
    account_number: d.account_number ?? "",
  };
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

function LabeledCell({
  name,
  label,
  ...props
}: { name: string; label: string } & React.ComponentProps<typeof FormikCell>) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={name} className="text-sm font-normal">
        {label}
      </Label>
      <FormikCell name={name} {...props} />
    </div>
  );
}

function ForWhoField({ married }: { married: boolean }) {
  const [field, , helpers] = useField<FileAs>("for_who");
  if (!married) return <p className="text-muted-foreground text-sm">Taxpayer</p>;
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as FileAs)}
    >
      {FILE_AS_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`fw-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`fw-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/** Live sum / balance readout (matches the PDF's derived boxes). */
function IaTotals() {
  const [total] = useField("total_amount");
  const [addTotal] = useField("add_total_amount");
  const [paid] = useField("tax_amt_pay");
  const sum = (Number(total.value) || 0) + (Number(addTotal.value) || 0);
  const balance = sum - (Number(paid.value) || 0);
  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="text-muted-foreground flex flex-wrap gap-6 text-sm">
      <span>
        Total tax owed: <span className="text-foreground font-medium">${fmt(sum)}</span>
      </span>
      <span>
        Balance after payment: <span className="text-foreground font-medium">${fmt(balance)}</span>
      </span>
    </div>
  );
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form 433-H IA header (combined CIS + Installment Agreement). The
 * financial body auto-fills from the worksheet layer server-side; only the IA
 * request header is entered here. Spanish variant shares this editor.
 */
export function Form433HEditor({ client, submission, onSubmissionChange }: Props) {
  const married = client.maritalStatus === "MARRIED";
  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-4 p-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-foreground font-semibold">Request for</span>
            <ForWhoField married={married} />
          </div>
          <p className="text-muted-foreground text-sm">
            {client.name} — asset, income and expense detail fills automatically from the
            financial worksheet.
          </p>
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Installment agreement request</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-3">
            <LabeledCell name="tax_form_no" label="Tax form" placeholder="1040" />
            <LabeledCell name="tax_periods" label="Tax periods" />
            <LabeledCell name="total_amount" label="Amount owed ($)" inputMode="decimal" />
            <LabeledCell name="add_tax_form_no" label="Additional tax form" />
            <LabeledCell name="add_tax_periods" label="Additional periods" />
            <LabeledCell name="add_total_amount" label="Additional amount ($)" inputMode="decimal" />
            <LabeledCell name="tax_amt_pay" label="Payment with this request ($)" inputMode="decimal" />
          </div>
          <IaTotals />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Payment terms</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="tax_pay_per_month" label="Monthly payment ($)" inputMode="decimal" />
            <div className="grid gap-1.5">
              <Label className="text-sm font-normal">Payment day of month</Label>
              <FormikSelect name="tax_pay_date" options={PAYMENT_DAYS} placeholder="Day" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="routing_number" label="Routing number (9 digits)" inputMode="numeric" maxLength={9} />
            <LabeledCell name="account_number" label="Account number (≤ 17 digits)" inputMode="numeric" maxLength={17} />
          </div>
          <div className="flex flex-wrap gap-6">
            <FormikCheckbox name="no_electronic" label="Unable to make electronic payments" />
            <FormikCheckbox name="payroll_deduction" label="Payroll deduction agreement" />
          </div>
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
